import { use, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { KeyedMutator } from 'swr';

import { EmptyState } from '~components';
import FormDialog from '~components/dialogs/formDialog';
import { EditUserForm, EditUserFormResults, UsersDataGrid, UsersService } from '~features/users';
import { OffsetPagedResults } from '~interfaces';
import User from '~interfaces/user';
import ApiError from '~services/apiError';
import { getService } from '~services/serviceFactory';

import OrganisationActionBar from '../components/organisationActionBar';
import OrganisationContext from '../providers/organisationDetailsContext';

type OrganisationUsersActions = {
	type: 'invite';
	usersMutate: KeyedMutator<OffsetPagedResults<User>>;
};

const usersService = getService(UsersService);

const OrganisationUsersContainer = () => {
	const { t } = useTranslation('entities');
	const { enqueueSnackbar } = useSnackbar();
	const organisationContext = use(OrganisationContext);
	const organisationId = organisationContext.organisation?.id ?? '0';
	const [userAction, setUserAction] = useState<OrganisationUsersActions | null>(null);
	const [isUpdatingUser, setIsUpdatingUser] = useState<boolean>(false);

	const clearUserAction = () => {
		setUserAction(null);
	};

	const inviteUser = (formData: EditUserFormResults) => {
		setIsUpdatingUser(true);
		usersService
			.inviteUser({ userData: formData, organisationId: organisationId })
			.then((_) => {
				enqueueSnackbar(t('user.messages.inviteUserSuccess', { ns: 'entities' }), {
					variant: 'success',
				});
				userAction?.usersMutate();
				clearUserAction();
			})
			.catch((error: ApiError) => {
				enqueueSnackbar(error.getDescription(), {
					variant: 'error',
				});
			})
			.finally(() => {
				setIsUpdatingUser(false);
			});
	};

	return (
		<>
			{organisationId != undefined ?
				<>
					<OrganisationActionBar organisationId={organisationId} showEditButton={false} />
					<UsersDataGrid
						showTitle
						showInviteButton
						overwrittenClientId={organisationId}
						handleInviteButtonClicked={(mutate) =>
							setUserAction({
								type: 'invite',
								usersMutate: mutate,
							})
						}
					/>
				</>
			:	<EmptyState
					icon={'icon/bar-chart-square'}
					title={t('noFilterResults', { ns: 'table' })}
					description={t('noFilterResultsDescription', { ns: 'table' })}
				/>
			}

			<FormDialog
				open={userAction?.type === 'invite'}
				title={t('user.labels.inviteUser', { ns: 'entities' })}
				loading={isUpdatingUser}
			>
				<EditUserForm
					allowRoleEditing={true}
					includeEmailField={true}
					onSubmit={async (formData) => inviteUser(formData)}
					onCancel={clearUserAction}
				/>
			</FormDialog>
		</>
	);
};

export default OrganisationUsersContainer;
